import { MouseEvent } from 'react';
import { FileManagement } from 'api/model';
import { DeleteOutline, Download } from '@mui/icons-material';
import { formatBytes, getFileIcon } from 'utils/helpers.tsx';
import { alpha, Box, Tooltip, Typography } from '@mui/material';
import useFileManagement from 'api/file-management/useFileManagement';
import useSingleFileManagment from 'api/file-management/useSingleFileManagment';
import InfoDialog from 'components/dialog/InfoDialog';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import useDialog from 'hooks/useDialog';
import { JsonView } from 'components/JsonView';
import ScopeIndicator from 'components/indicators/ScopeIndicator';
import { ActionIconButton } from 'components/button/ActionIconButton';
import { EntityCard } from 'components/settingsHub/EntityCard';

type Props = {
  file: FileManagement;
};

const FileItem = ({ file }: Props) => {
  const { download } = useFileManagement();
  const { remove } = useSingleFileManagment(file.id);
  const [showFileDialog, FileDialog] = useDialog(InfoDialog);
  const [showDeleteDialog, DeleteDialog] = useDialog(ConfirmationDialog);

  const fileIcon = getFileIcon(file.mime_type, file.file_name, 46);
  const fileSizeFormatted = formatBytes(file.file_size);
  const isGlobal = !file.project_id;
  const columnCount = file.meta?.content?.[0]?.columns?.length;
  const validation = file.meta?.validation_result;
  const displayName = file.file_name.length > 20 ? `${file.file_name.substring(0, 17)}...` : file.file_name;

  const stopPropagation = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <EntityCard onClick={() => showFileDialog(true)} ariaLabel={`View file ${file.file_name}`} minHeight={224}>
      <ScopeIndicator isGlobal={isGlobal} className="absolute left-3 top-3 z-10" />
      <div className="entity-card-actions absolute top-2 right-2 z-10 flex gap-1 opacity-0 transition-opacity">
        <ActionIconButton
          size="small"
          color="info"
          title="Download"
          onClick={stopPropagation(() => download.mutate(file.id))}
        >
          <Download fontSize="small" />
        </ActionIconButton>
        <ActionIconButton
          size="small"
          color="error"
          title="Delete"
          onClick={stopPropagation(() => showDeleteDialog(true))}
        >
          <DeleteOutline fontSize="small" />
        </ActionIconButton>
      </div>

      <div className="flex h-full flex-col items-center justify-evenly gap-2 py-3 text-center">
        <div>{fileIcon}</div>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: '0.95rem' }}
          className="line-clamp-2 px-3"
          title={file.file_name}
        >
          {displayName}
        </Typography>
        <div className="flex items-center justify-center gap-3">
          <Typography variant="caption" color="text.secondary">
            {fileSizeFormatted}
          </Typography>
          {columnCount != null && (
            <Typography variant="caption" color="text.secondary">
              {columnCount} cols
            </Typography>
          )}
        </div>
        {validation && (
          <Tooltip title={validation.valid ? 'Schema validation passed' : 'Schema validation failed'} arrow>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: '0.72rem',
                fontWeight: 600,
                color: validation.valid ? 'success.main' : 'error.main',
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'currentColor',
                  boxShadow: (t) =>
                    `0 0 0 3px ${alpha(validation.valid ? t.palette.success.main : t.palette.error.main, 0.2)}`,
                }}
              />
              {validation.valid ? 'Valid' : 'Invalid'}
            </Box>
          </Tooltip>
        )}
      </div>

      <FileDialog title={`File: ${file.file_name}`}>
        <Typography variant="body2">
          <JsonView src={file} name="file" />
        </Typography>
      </FileDialog>
      <DeleteDialog
        title="Delete File"
        onConfirm={() => remove.mutate()}
        buttonsSettings={{ confirmText: 'Delete', confirmColor: 'error' }}
      >
        <Typography>
          Are you sure you want to delete the file <strong>{file.file_name}</strong>? This action cannot be undone.
        </Typography>
      </DeleteDialog>
    </EntityCard>
  );
};

export default FileItem;
